import { useCallback, useMemo, useState } from 'react';
import { useSeo } from '@/hooks/useSeo';
import { collections, faqs, siteInfo } from '@/mocks/home';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Highlights from './components/Highlights';
import Collections from './components/Collections';
import Categories from './components/Categories';
import Featured from './components/Featured';
import Faq from './components/Faq';
import About from './components/About';
import Contact from './components/Contact';
import Footer from './components/Footer';
import BackToTop from './components/BackToTop';

export default function Home() {
  const [activeFilter, setActiveFilter] = useState('all');

  const handleSelectCategory = useCallback((categoryId: string) => {
    setActiveFilter(categoryId);
    const target = document.getElementById('collections');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const canonical =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}`
      : 'https://17cguasite.example.com/';

  const seo = useMemo(
    () => ({
      title: '17C吃瓜站｜现场视频·录屏片段·直播回放影像合集',
      description:
        '17C吃瓜站整理娱乐热点的现场视频、录屏片段与直播回放，按专题归档并做高清画质修复，汇总最全的影像资料，让你直观感受现场氛围，不错过任何精彩画面。',
      keywords: '17c吃瓜,吃瓜视频合集,现场视频,录屏片段,直播回放,画面截图,娱乐影像资料',
      canonical,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteInfo.name,
          url: canonical,
          description: siteInfo.intro,
          inLanguage: 'zh-CN',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: siteInfo.name,
          url: canonical,
          email: siteInfo.email,
        },
        {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '17C吃瓜站首页',
          url: canonical,
          inLanguage: 'zh-CN',
          description:
            '汇总娱乐热点的现场视频、录屏片段、直播回放与高清截图，按主题专题持续更新。',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: '热门影像合集',
          itemListElement: collections.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.title,
          })),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      ],
    }),
    [canonical],
  );

  useSeo(seo);

  return (
    <div className="w-full min-h-screen bg-background-50">
      <Navbar />
      <main className="w-full">
        <Hero />
        <Highlights />
        <Collections activeFilter={activeFilter} onFilterChange={setActiveFilter} />
        <Categories onSelect={handleSelectCategory} />
        <Featured />
        <Faq />
        <About />
        <Contact />
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}